// Údaje klientky do smlouvy před podpisem (3. 11. 2026, schválený mockup): jméno, e‑mail, telefon a adresa.
// Trenér je zkontroluje s klientkou, případně upraví. Úprava se uloží do kontaktu, fakturační údaje zůstanou.
// Fakturace na firmu: do smlouvy patří osobní adresa klientky, bez ní podpis nepustí.
import React, { useEffect, useState } from "react";
import { Pencil, ArrowRight, Loader2 } from "lucide-react";
import { apiFetch } from "../../lib/api.js";
import { Oznameni, Pil } from "../sales/salesUi.jsx";

const API = "/api/smlouva-podpis";

function Pole({ id, label, value, onChange, typ = "text", autoComplete }) {
  return (
    <label className="block space-y-1" htmlFor={id}>
      <span className="block text-[13px] font-semibold text-slate-600">{label}</span>
      <input id={id} type={typ} value={value || ""} autoComplete={autoComplete} onChange={(e) => onChange(e.target.value)}
        className="min-h-[46px] w-full rounded-xl border border-slate-300 bg-white px-3 text-[16px] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-200" />
    </label>
  );
}

const adresaText = (a) => (a && a.ulice && a.mesto && a.psc ? `${a.ulice}, ${a.mesto}, ${a.psc}` : "");
const kompletni = (u) => !!(u && u.jmeno && /@/.test(u.email || "") && String(u.telefon || "").replace(/\D/g, "").length >= 9 && adresaText(u.adresa));

// klic: { zkusebni_id } | { deal_id }. onPotvrzeno(udaje) se zavolá po „Údaje sedí“ (rezim "qr" jen předá údaje,
// rezim "ulozit" je rovnou uloží do kontaktu, používá podpis na dálku).
export default function UdajeSmlouvy({ klic, tlacitko = "Údaje sedí, ukázat QR kód", rezim = "qr", onPotvrzeno, potvrzeno = false, onZmena }) {
  const [u, setU] = useState(null);
  const [firma, setFirma] = useState(false);
  const [uprava, setUprava] = useState(false);
  const [chyba, setChyba] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let zive = true;
    apiFetch(`${API}?${new URLSearchParams({ akce: "udaje", ...klic }).toString()}`).then(async (r) => {
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Údaje se nepodařilo načíst.");
      if (!zive) return;
      setU(j.udaje); setFirma(!!j.firma);
      if (j.adresa_chybi || !kompletni(j.udaje)) setUprava(true);
    }).catch((e) => zive && setChyba(e.message));
    return () => { zive = false; };
  }, [klic.zkusebni_id, klic.deal_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const nastav = (k) => (v) => { setU((x) => ({ ...x, [k]: v })); onZmena?.(); };
  const nastavA = (k) => (v) => { setU((x) => ({ ...x, adresa: { ...(x.adresa || {}), [k]: v } })); onZmena?.(); };

  const potvrdit = async () => {
    setBusy(true); setChyba("");
    try {
      if (rezim === "ulozit") {
        const r = await apiFetch(API, { method: "POST", body: JSON.stringify({ akce: "udaje", ...klic, udaje: u }) });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Údaje se nepodařilo uložit.");
        setU(j.udaje); setUprava(false);
        await onPotvrzeno?.(j.udaje);
      } else {
        await onPotvrzeno?.(u);
      }
    } catch (e) { setChyba(e.message); } finally { setBusy(false); }
  };

  if (!u) return chyba ? <Oznameni ton="problem">{chyba}</Oznameni> : <div className="flex items-center gap-2 py-4 text-[14px] text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Načítám údaje…</div>;

  return (
    <div className="rounded-2xl border border-slate-200 p-4 sm:p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="text-[15px] font-bold text-slate-900">Údaje klientky do smlouvy</div>
        {!uprava && !potvrzeno && <button type="button" className="btn-secondary" onClick={() => { setUprava(true); onZmena?.(); }}><Pencil className="h-4 w-4" />Upravit</button>}
      </div>
      {!uprava ? (
        <div className="flex flex-wrap gap-2">
          <Pil ton="neutralni" casti={["Jméno", u.jmeno]} />
          <Pil ton="neutralni" casti={["E‑mail", u.email]} />
          <Pil ton="neutralni" casti={["Telefon", u.telefon]} />
          {/* Adresa bývá dlouhá: vlastní pilulka, která se smí zalomit (na telefonu by jinak přetekla). */}
          <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[13px] font-bold text-slate-700">
            Adresa<span aria-hidden className="h-3 w-px shrink-0 bg-current opacity-40" /><span className="min-w-0 break-words">{adresaText(u.adresa)}</span>
          </span>
        </div>
      ) : (
        <div className="space-y-3">
          {firma && <Oznameni ton="pozor">Fakturace je <strong>na firmu</strong>. Do smlouvy patří <strong>osobní adresa klientky</strong>, doplň ji prosím.</Oznameni>}
          <div className="grid gap-3 sm:grid-cols-2">
            <Pole id="s-jmeno" label="Jméno a příjmení" value={u.jmeno} onChange={nastav("jmeno")} autoComplete="name" />
            <Pole id="s-tel" label="Telefon (přijde na něj SMS k podpisu)" typ="tel" value={u.telefon} onChange={nastav("telefon")} autoComplete="tel" />
          </div>
          <Pole id="s-email" label="E‑mail (pošleme na něj podepsanou smlouvu)" typ="email" value={u.email} onChange={nastav("email")} autoComplete="email" />
          <div className="grid gap-3 sm:grid-cols-[2fr_1.4fr_1fr]">
            <Pole id="s-ulice" label="Ulice a číslo" value={u.adresa?.ulice} onChange={nastavA("ulice")} autoComplete="street-address" />
            <Pole id="s-mesto" label="Město" value={u.adresa?.mesto} onChange={nastavA("mesto")} autoComplete="address-level2" />
            <Pole id="s-psc" label="PSČ" value={u.adresa?.psc} onChange={nastavA("psc")} autoComplete="postal-code" />
          </div>
          <p className="text-[13px] text-slate-500">Úprava se uloží i do kontaktu klientky. Fakturační údaje zůstanou, jak jsou.</p>
        </div>
      )}
      {chyba && <Oznameni ton="problem" className="mt-3">{chyba}</Oznameni>}
      {potvrzeno
        ? <Oznameni ton="ok" className="mt-3"><strong>Údaje jsou potvrzené.</strong> Po uzavření klientce přijde odkaz k podpisu SMS a e‑mailem.</Oznameni>
        : <button type="button" className="btn-primary mt-4 w-full justify-center sm:w-auto" disabled={!kompletni(u) || busy} onClick={potvrdit}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{tlacitko}<ArrowRight className="h-4 w-4" />
          </button>}
    </div>
  );
}
